import { Link } from "react-router-dom";
import { ArrowLeft, Shield, Lock, Eye, Trash2 } from "lucide-react";
import Footer from "../components/Footer";

export default function PrivacyPolicyPage() {
  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <nav className="bg-white border-b border-slate-100 sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-6 py-4">
          <Link to="/" className="flex items-center gap-2 text-slate-600 hover:text-[#00B894] transition-colors">
            <ArrowLeft className="w-5 h-5" />
            <span>Back to Home</span>
          </Link>
        </div>
      </nav>

      <main className="max-w-4xl mx-auto px-6 py-12">
        <div className="mb-8">
          <img src="/images/gigseasy-logo.svg" alt="GigsEasy" className="h-10 mb-6" />
          <h1 className="text-4xl font-bold text-slate-900 mb-2">Privacy Policy</h1>
          <p className="text-slate-500">Last Updated: March 20, 2026</p>
          <p className="text-slate-500">Effective Date: March 20, 2026</p>
        </div>

        {/* Privacy at a Glance */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-12">
          <div className="bg-slate-50 p-4 rounded-lg text-center">
            <Eye className="w-8 h-8 text-[#00B894] mx-auto mb-2" />
            <p className="font-semibold text-slate-900 text-sm">Your Data, Your Control</p>
            <p className="text-xs text-slate-500">Export or delete anytime</p>
          </div>
          <div className="bg-slate-50 p-4 rounded-lg text-center">
            <Lock className="w-8 h-8 text-[#00B894] mx-auto mb-2" />
            <p className="font-semibold text-slate-900 text-sm">Encrypted & Secure</p>
            <p className="text-xs text-slate-500">Industry-standard encryption</p>
          </div>
          <div className="bg-slate-50 p-4 rounded-lg text-center">
            <Shield className="w-8 h-8 text-[#00B894] mx-auto mb-2" />
            <p className="font-semibold text-slate-900 text-sm">No Data Selling</p>
            <p className="text-xs text-slate-500">We never sell your data</p>
          </div>
          <div className="bg-slate-50 p-4 rounded-lg text-center">
            <Trash2 className="w-8 h-8 text-[#00B894] mx-auto mb-2" />
            <p className="font-semibold text-slate-900 text-sm">Minimal Collection</p>
            <p className="text-xs text-slate-500">Only what we need</p>
          </div>
        </div>

        <div className="prose prose-slate max-w-none">
          <p className="text-slate-600 mb-8">
            GigsEasy.com, a service owned and operated by <strong>Active Consumers LLC</strong> ("we," "us," or "our"), 
            is committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and 
            safeguard your information when you use our website, mobile application, and services (collectively, the "Service"). 
            Please read this policy carefully to understand our practices regarding your personal data.
          </p>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-slate-900 mb-4">1. Information We Collect</h2>
            
            <h3 className="text-xl font-semibold text-slate-800 mb-3">1.1 Information You Provide Directly</h3>
            <p className="text-slate-600 mb-4">We collect information you voluntarily provide when using our Service:</p>
            <ul className="list-disc pl-6 text-slate-600 space-y-2 mb-4">
              <li><strong>Account Information:</strong> Name, email address, password (hashed), phone number (optional), country, and social media profile links when you register or update your account</li>
              <li><strong>Task Data:</strong> Information about your task completions, submissions, and activity on the platform</li>
              <li><strong>Payment Information:</strong> When you request withdrawals, our payment processor (PayPal) collects billing information. We do not store complete payment details on our servers</li>
              <li><strong>Communications:</strong> Information you provide when contacting our support team, including email content</li>
            </ul>

            <h3 className="text-xl font-semibold text-slate-800 mb-3">1.2 Information Collected Automatically</h3>
            <p className="text-slate-600 mb-4">When you access or use our Service, we automatically collect:</p>
            <ul className="list-disc pl-6 text-slate-600 space-y-2 mb-4">
              <li><strong>Device Information:</strong> Browser type, operating system, device type, unique device identifiers</li>
              <li><strong>Log Data:</strong> IP address, access times, pages viewed, referring URL</li>
              <li><strong>Usage Data:</strong> Features used, actions taken, time spent on pages</li>
              <li><strong>Location Data:</strong> General geographic location based on IP address (city/country level only)</li>
              <li><strong>Cookies:</strong> We use cookies and similar technologies to collect information about your browsing activities</li>
            </ul>

            <h3 className="text-xl font-semibold text-slate-800 mb-3">1.3 Information from Third Parties</h3>
            <ul className="list-disc pl-6 text-slate-600 space-y-2">
              <li><strong>Social Login Providers:</strong> If you sign in using Google, we receive your name, email, and profile picture</li>
              <li><strong>Payment Processors:</strong> Transaction status from PayPal</li>
              <li><strong>Fraud Prevention:</strong> Risk scores and fraud indicators from verification services</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-slate-900 mb-4">2. How We Use Your Information</h2>
            
            <h3 className="text-xl font-semibold text-slate-800 mb-3">2.1 Providing and Improving Our Service</h3>
            <ul className="list-disc pl-6 text-slate-600 space-y-2 mb-4">
              <li>Creating and managing your account</li>
              <li>Processing task completions and awarding points</li>
              <li>Processing withdrawals and payments</li>
              <li>Providing customer support</li>
              <li>Analyzing usage patterns to improve features</li>
            </ul>

            <h3 className="text-xl font-semibold text-slate-800 mb-3">2.2 Communications</h3>
            <ul className="list-disc pl-6 text-slate-600 space-y-2 mb-4">
              <li>Sending transactional emails (account verification, task notifications, payment confirmations)</li>
              <li>Sending service announcements and updates</li>
              <li>Marketing communications (with your consent, and you can opt out anytime)</li>
            </ul>

            <h3 className="text-xl font-semibold text-slate-800 mb-3">2.3 Security and Legal Compliance</h3>
            <ul className="list-disc pl-6 text-slate-600 space-y-2">
              <li>Detecting, preventing, and addressing fraud and security issues</li>
              <li>Enforcing our Terms of Use</li>
              <li>Complying with legal obligations</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-slate-900 mb-4">3. How We Share Your Information</h2>
            <p className="text-slate-600 mb-4 font-semibold">We do not sell your personal information.</p>
            <p className="text-slate-600 mb-4">We may share your information with:</p>
            
            <div className="overflow-x-auto mb-4">
              <table className="min-w-full border border-slate-200 rounded-lg">
                <thead className="bg-slate-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-slate-900">Provider</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-slate-900">Purpose</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-slate-900">Data Shared</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-200">
                  <tr>
                    <td className="px-4 py-3 text-sm text-slate-600">PayPal</td>
                    <td className="px-4 py-3 text-sm text-slate-600">Payment processing</td>
                    <td className="px-4 py-3 text-sm text-slate-600">Email, withdrawal amount</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-3 text-sm text-slate-600">Postmark</td>
                    <td className="px-4 py-3 text-sm text-slate-600">Email delivery</td>
                    <td className="px-4 py-3 text-sm text-slate-600">Email address, name</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-3 text-sm text-slate-600">MongoDB Atlas</td>
                    <td className="px-4 py-3 text-sm text-slate-600">Data storage</td>
                    <td className="px-4 py-3 text-sm text-slate-600">All user data (encrypted)</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-3 text-sm text-slate-600">Google OAuth</td>
                    <td className="px-4 py-3 text-sm text-slate-600">Authentication</td>
                    <td className="px-4 py-3 text-sm text-slate-600">Email, name, profile picture</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <p className="text-slate-600 mb-4">We may also disclose information:</p>
            <ul className="list-disc pl-6 text-slate-600 space-y-2">
              <li>If required by law or in response to valid legal requests</li>
              <li>In the event of a merger, acquisition, or sale of assets</li>
              <li>With your explicit consent</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-slate-900 mb-4">4. Communications and TCPA Compliance</h2>
            <p className="text-slate-600 mb-4">
              We are committed to complying with the Telephone Consumer Protection Act (TCPA) and other applicable 
              communications laws.
            </p>
            
            <h3 className="text-xl font-semibold text-slate-800 mb-3">4.1 Email Communications</h3>
            <p className="text-slate-600 mb-4">
              By providing your email address and consenting during registration, you agree to receive:
            </p>
            <ul className="list-disc pl-6 text-slate-600 space-y-2 mb-4">
              <li><strong>Transactional emails:</strong> Account verification, task notifications, payment confirmations, security alerts</li>
              <li><strong>Marketing emails (with separate consent):</strong> Promotional offers, new features, newsletters</li>
            </ul>

            <h3 className="text-xl font-semibold text-slate-800 mb-3">4.2 Your Communication Rights</h3>
            <ul className="list-disc pl-6 text-slate-600 space-y-2 mb-4">
              <li>Consent to receive communications is not a condition for using GigsEasy.com services</li>
              <li>You may receive up to 4 marketing messages per month</li>
              <li>You can opt out of marketing communications at any time by clicking "unsubscribe" or contacting support@gigseasy.com</li>
              <li>Transactional messages necessary for service delivery cannot be opted out while maintaining an active account</li>
            </ul>

            <h3 className="text-xl font-semibold text-slate-800 mb-3">4.3 Data Protection for Communications</h3>
            <p className="text-slate-600">
              All information collected during the opt-in process will not be sold, rented, or shared with third parties 
              for their marketing purposes. Your communication preferences are stored securely and honored immediately.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-slate-900 mb-4">5. Data Security</h2>
            <p className="text-slate-600 mb-4">We implement appropriate security measures:</p>
            <ul className="list-disc pl-6 text-slate-600 space-y-2">
              <li><strong>Encryption:</strong> All data is encrypted in transit (TLS 1.3) and at rest (AES-256)</li>
              <li><strong>Password Security:</strong> Passwords are hashed using bcrypt with salt</li>
              <li><strong>Access Controls:</strong> Strict access controls for all systems</li>
              <li><strong>Regular Audits:</strong> Periodic security assessments</li>
            </ul>
            <p className="text-slate-600 mt-4">
              However, no method of transmission over the Internet is 100% secure. While we strive to protect your 
              information, we cannot guarantee absolute security.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-slate-900 mb-4">6. Data Retention</h2>
            <ul className="list-disc pl-6 text-slate-600 space-y-2">
              <li><strong>Active Accounts:</strong> Data retained while your account is active</li>
              <li><strong>Deleted Accounts:</strong> Data deleted within 30 days of account deletion request</li>
              <li><strong>Backup Retention:</strong> Encrypted backups retained for up to 90 days</li>
              <li><strong>Legal Holds:</strong> Data may be retained longer if required for legal proceedings</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-slate-900 mb-4">7. Your Privacy Rights</h2>
            <p className="text-slate-600 mb-4">Depending on your location, you may have the following rights:</p>
            <ul className="list-disc pl-6 text-slate-600 space-y-2">
              <li><strong>Access and Portability:</strong> Request a copy of your personal information</li>
              <li><strong>Correction:</strong> Update or correct your personal information</li>
              <li><strong>Deletion:</strong> Request deletion of your personal information</li>
              <li><strong>Marketing Opt-Out:</strong> Opt out of marketing communications anytime</li>
            </ul>
            <p className="text-slate-600 mt-4">
              You can exercise most of these rights directly from your account settings or by contacting support@gigseasy.com.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-slate-900 mb-4">8. California Privacy Rights (CCPA/CPRA)</h2>
            <p className="text-slate-600 mb-4">
              If you are a California resident, you have additional rights under the California Consumer Privacy Act (CCPA) 
              and California Privacy Rights Act (CPRA). Please see our{" "}
              <Link to="/california-privacy" className="text-[#00B894] hover:underline">California Privacy Notice</Link>{" "}
              for detailed information about:
            </p>
            <ul className="list-disc pl-6 text-slate-600 space-y-2">
              <li>Categories of personal information we collect</li>
              <li>Your right to know, delete, and correct</li>
              <li>Your right to non-discrimination</li>
              <li>How to submit verifiable consumer requests</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-slate-900 mb-4">9. International Data Transfers</h2>
            <p className="text-slate-600">
              Your information may be transferred to and processed in countries other than your country of residence, 
              including the United States. We implement appropriate safeguards for international transfers, including 
              Standard Contractual Clauses and Data Processing Agreements with all service providers.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-slate-900 mb-4">10. Children's Privacy</h2>
            <p className="text-slate-600">
              Our Service is not intended for children under 18 years of age. We do not knowingly collect personal 
              information from children under 18. If you are a parent or guardian and believe your child has provided 
              us with personal information, please contact us immediately.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-slate-900 mb-4">11. Changes to This Privacy Policy</h2>
            <p className="text-slate-600">
              We may update this Privacy Policy from time to time. We will notify you of any material changes by 
              posting the new Privacy Policy on this page and updating the "Last Updated" date. For significant changes, 
              we will also send you an email notification.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-slate-900 mb-4">12. Contact Us</h2>
            <p className="text-slate-600 mb-4">
              If you have any questions about this Privacy Policy or our data practices, please contact us:
            </p>
            <div className="bg-slate-50 p-6 rounded-lg">
              <p className="font-semibold text-slate-900">Active Consumers LLC</p>
              <p className="text-slate-600">San Juan, Puerto Rico 00901</p>
              <p className="text-slate-600">Email: support@gigseasy.com</p>
              <p className="text-slate-600 mt-2">Privacy Team: support@gigseasy.com</p>
            </div>
            <p className="text-slate-500 mt-4 text-sm">
              We aim to respond to all privacy-related inquiries within 30 days.
            </p>
          </section>
        </div>
      </main>

      <Footer />
    </div>
  );
}
